import { CardContainer, Picture, TypographyV2 } from '@which/seatbelt'

import type {
  ProductListingPageQuery,
  ProductRecommendationsQuery,
} from '../../../../../generated/frontend'
import { analytics } from '../../Configs/analytics'
import type { Category } from '../../Configs/categories'
import { useGuidedPurchaseItemSpec } from '../../hooks/useGuidedPurchaseItemSpec'
import styles from '../ResultsScreen.module.scss'

type OldFiltersProduct =
  ProductListingPageQuery['productListingPage']['productsWithPagination']['products'][number]
type CAPIProduct = ProductRecommendationsQuery['productRecommendations']['items'][number]

export type Products = Array<OldFiltersProduct | CAPIProduct>

interface RecommendationResultsProps {
  products: Products
  purchaseJourneyCategory: Category
}

const TitleContainer = ({ manufacturer, model }: { manufacturer?: string; model?: string }) => (
  <div className={styles.TitleContainer}>
    {manufacturer && (
      <TypographyV2
        tag="h3"
        textStyle="sb-text-interface-body-small-strong"
        className={styles.TitleContainerManufacturer}
      >
        {manufacturer}
      </TypographyV2>
    )}

    {model && (
      <TypographyV2
        textStyle="sb-text-interface-body-x-small-regular"
        className={styles.TitleContainerModel}
      >
        {model}
      </TypographyV2>
    )}
  </div>
)

const ScoreContainer = ({ overallScore }: { overallScore?: number }) => (
  <div className={styles.ScoreContainer}>
    <TypographyV2
      tag="span"
      textStyle="sb-text-interface-body-x-small-regular"
      className={styles.ScoreContainerTitle}
    >
      Which? Score
    </TypographyV2>
    <TypographyV2
      tag="span"
      textStyle="sb-text-interface-heading-medium"
      className={styles.ScoreContainerValue}
    >
      {`${overallScore}%`}
    </TypographyV2>
  </div>
)

const ProductDetailsContainer = ({
  price,
  overallScore,
}: {
  price?: string
  overallScore?: number
}) => (
  <div className={styles.ProductDetailsContainer}>
    {price && (
      <TypographyV2
        textStyle="sb-text-heading-small"
        className={styles.ProductDetailsContainerPrice}
      >
        {price}
      </TypographyV2>
    )}

    {overallScore && <ScoreContainer overallScore={overallScore} />}
  </div>
)

const ProductRecommendationCard = ({
  product,
  index,
  purchaseJourneyCategory,
}: {
  product: Products[number]
  index: number
  purchaseJourneyCategory: Category
}) => {
  const { manufacturer, model, category, slug, image, price, overallScore } = product
  const categorySlug = category?.toLowerCase()
  const url = categorySlug && slug ? `/reviews/${categorySlug}/${slug}` : '#'
  const itemSpec = useGuidedPurchaseItemSpec()

  return (
    <CardContainer
      arrangement="horizontal"
      primaryLink={url}
      ariaLabel={`View details for ${manufacturer} ${model}`}
      trackingData={{
        target: '_blank',
        rel: 'noopener noreferrer',
        'data-action-group': 'onward journey',
        'data-item-text': `${manufacturer} ${model}`,
        'data-item-parent-text': 'Our recommendation',
        'data-item-index': String(index + 1),
        'data-item-group': 'personalisation tools',
        'data-product-id': product.businessKey,
        'data-which-id': 'personalisation-tool-onward-journey',
        'data-item-spec': itemSpec,
        'data-tool-name': analytics[purchaseJourneyCategory].tool_name,
      }}
    >
      <TitleContainer manufacturer={manufacturer} model={model} />

      {image && (
        <Picture
          src={image.src}
          sources={image.sources}
          alt={`${manufacturer} ${model}`}
          className={styles.CardImage}
        />
      )}

      <ProductDetailsContainer price={price} overallScore={overallScore} />
    </CardContainer>
  )
}

export const RecommendationResults = ({
  products,
  purchaseJourneyCategory,
}: RecommendationResultsProps) => {
  const topTwoProducts = products.slice(0, 2)

  return (
    <div className={styles.recommendationResults}>
      {topTwoProducts.map((product, index) => (
        <ProductRecommendationCard
          key={index}
          product={product}
          index={index}
          purchaseJourneyCategory={purchaseJourneyCategory}
        />
      ))}
    </div>
  )
}
